import { sendGAEvent } from '@noths/polaris-client-google-analytics';
import { EventsMap } from '@noths/polaris-client-next-redux';

import { DELIVERY_QUICK_FILTERS_NAME } from 'src/constants/elementIds';
import { ReduxApplicationState } from 'src/redux/combinedReducer';
import { actions as visibilityTrackingActions } from 'src/redux/visibilityTracking';
import { EventCategory, FilterOptionValue, SelectedFilterOption } from 'src/types';
import { FilterUid } from '../constants/uids';
import { onExpandableContentToggle, onViewResultsClick } from '../modules/actions';
import {
  ALL_CATEGORIES_DEPTH_VALUE,
  FilterOptionSelectionTypes,
  selectDepthOfSelectedFilterOption,
  selectFilterOptionSelection,
  selectFilters,
  selectTotalFiltersAppliedCount,
  selectTotalSelectedFilterOptionsCount,
} from '../modules/selectors';
import { actions } from '../modules/slice';
import {
  AllFiltersViewedImpression,
  CustomFilterEvent,
  DeliveryFilterImpression,
  DeliveryQuickFilterImpression,
  FILTER_LIST_ITEM_VISIBILITY_NAME,
  FilterListItemImpressionActionReturn,
} from './events';

const getFilterCountEventLabel = (state: ReduxApplicationState) => {
  const totalFilters = selectTotalFiltersAppliedCount(state);
  const totalFilterOptions = selectTotalSelectedFilterOptionsCount(state);

  return `number of categories: ${totalFilters}|number of filters: ${totalFilterOptions}`;
};

const getTreeFilterEventData = (
  depth: number,
  filterTitle: string,
  filterOptionTitle: string,
  state: ReduxApplicationState,
  actionPayload: SelectedFilterOption,
): { eventAction: string; eventLabel: string } => {
  if (depth === ALL_CATEGORIES_DEPTH_VALUE) {
    return {
      eventAction: `back to all ${filterTitle}`,
      eventLabel: getFilterCountEventLabel(state),
    };
  }

  const currentDepth = selectDepthOfSelectedFilterOption(state, actionPayload);

  const eventAction =
    currentDepth && currentDepth > depth
      ? `back to level ${depth} ${filterTitle}`
      : `select level ${depth} ${filterTitle}`;

  return {
    eventAction,
    eventLabel: filterOptionTitle,
  };
};

export const GAEventsMap: EventsMap<ReduxApplicationState> = {
  [visibilityTrackingActions.getSectionVisibleActionType(DELIVERY_QUICK_FILTERS_NAME)]: () => {
    sendGAEvent<DeliveryQuickFilterImpression>({
      event: 'custom_event',
      event_category: 'Quick filters',
      event_action: 'quick filter impression',
      event_label: 'delivery',
    });
  },
  [visibilityTrackingActions.getSectionVisibleActionType(FILTER_LIST_ITEM_VISIBILITY_NAME)]: (
    state,
    _,
    action,
  ) => {
    const {
      payload: { filterUid },
    } = action as FilterListItemImpressionActionReturn;

    const filters = selectFilters(state);
    const filterIndex = filters.findIndex(({ uid }) => uid === filterUid);
    const isLastFilter = filterIndex === filters.length - 1;

    if (filterUid === FilterUid.Delivery) {
      sendGAEvent<DeliveryFilterImpression>({
        event: 'custom_event',
        event_category: 'Filters',
        event_action: 'filter impression',
        event_label: 'delivery',
      });
    }

    if (isLastFilter) {
      sendGAEvent<AllFiltersViewedImpression>({
        event: 'custom_event',
        event_category: 'Filters',
        event_action: 'filter impression',
        event_label: 'all filters viewed',
      });
    }
  },
  [onViewResultsClick.type]: (prevState) => {
    sendGAEvent<CustomFilterEvent>({
      event: 'custom_event',
      event_category: 'Filters',
      event_action: 'view results',
      event_label: getFilterCountEventLabel(prevState),
    });
  },
  [onExpandableContentToggle.type]: (prevState, _nextState, action) => {
    const {
      payload: { isExpanded, filterTitle },
    } = action as ReturnType<typeof onExpandableContentToggle>;

    sendGAEvent<CustomFilterEvent>({
      event: 'custom_event',
      event_category: 'Filters',
      event_action: `show ${isExpanded ? 'less' : 'all'} ${filterTitle}`,
      event_label: getFilterCountEventLabel(prevState),
    });
  },
  [actions.openModal.type]: (prevState) => {
    sendGAEvent<CustomFilterEvent>({
      event: 'custom_event',
      event_category: 'Filters',
      event_action: 'open modal',
      event_label: getFilterCountEventLabel(prevState),
    });
  },
  [actions.selectFilter.type]: (prevState, _nextState, action) => {
    const {
      payload: { title },
    } = action as ReturnType<typeof actions.selectFilter>;

    sendGAEvent<CustomFilterEvent>({
      event: 'custom_event',
      event_category: 'Filters',
      event_action: `open ${title}`,
      event_label: getFilterCountEventLabel(prevState),
    });
  },
  [actions.resetFilters.type]: (prevState) => {
    sendGAEvent<CustomFilterEvent>({
      event: 'custom_event',
      event_category: 'Filters',
      event_action: 'reset all filters',
      event_label: getFilterCountEventLabel(prevState),
    });
  },
  [actions.resetFilterOptions.type]: (prevState, _nextState, action) => {
    const {
      payload: { title },
    } = action as ReturnType<typeof actions.resetFilterOptions>;

    sendGAEvent<CustomFilterEvent>({
      event: 'custom_event',
      event_category: 'Filters',
      event_action: `reset ${title}`,
      event_label: getFilterCountEventLabel(prevState),
    });
  },
  [actions.selectFilterOption.type]: (prevState, _nextState, action) => {
    const { payload } = action as ReturnType<typeof actions.selectFilterOption>;
    const { filterOptionDepth, eventCategory: filterVariant = EventCategory.Filters } = payload;
    const { type, filterTitle, filterOptionTitle } = selectFilterOptionSelection(
      prevState,
      payload,
    );

    const eventActionMap: Record<EventCategory, Record<FilterOptionSelectionTypes, string>> = {
      [EventCategory.Filters]: {
        SELECT: `select filter`,
        DESELECT: `de-select filter`,
      },
      [EventCategory.QuickFilters]: {
        SELECT: `select quick filter`,
        DESELECT: `de-select quick filter`,
      },
    };

    let eventAction: string;
    let eventLabel: FilterOptionValue;

    if (filterOptionDepth) {
      ({ eventAction, eventLabel } = getTreeFilterEventData(
        filterOptionDepth,
        filterTitle,
        filterOptionTitle,
        prevState,
        payload,
      ));
    } else {
      eventAction = `${eventActionMap[filterVariant][type]} ${filterTitle}`;
      eventLabel = filterOptionTitle;
    }

    sendGAEvent<CustomFilterEvent>({
      event: 'custom_event',
      event_category: filterVariant,
      event_action: eventAction,
      event_label: eventLabel,
    });
  },
};
